import React, { useEffect, useState } from 'react';
import { Loader2, Search, Trash2 } from 'lucide-react';
import api from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const ROLE_OPTIONS = ['admin', 'facilitator', 'content_contributor', 'ashram_coord', 'center_coord', 'member'];
const DURATIONS = [['none', 'No expiry'], ['month', '1 month'], ['year', '1 year'], ['5year', '5 years']];

const MembershipCell = ({ user, plans, onApply }) => {
  const current = user.membership?.tier || 'basic';
  const [tier, setTier] = useState(current);
  const [dur, setDur] = useState('none');
  const [busy, setBusy] = useState(false);
  const changed = tier !== current;
  const paid = tier !== 'basic';
  const apply = async () => { setBusy(true); await onApply(user.user_id, tier, dur); setBusy(false); };
  const exp = user.membership?.expires_at ? new Date(user.membership.expires_at).toLocaleDateString() : null;
  // Options come from the live membership plans (real names) + a "no membership" option.
  const options = [{ tier_key: 'basic', name: 'Seekers Community (Free)' }, ...plans];
  return (
    <div className="flex flex-col gap-1.5">
      <select value={tier} onChange={(e) => setTier(e.target.value)} data-testid={`user-tier-${user.user_id}`}
        className="text-xs bg-white border border-rose/50 rounded-lg px-2 py-1 text-burgundy max-w-[190px]">
        {options.map((p) => <option key={p.tier_key} value={p.tier_key}>{p.name}</option>)}
      </select>
      {!changed && exp && <span className="text-[10px] text-mahogany/50">exp {exp}</span>}
      {changed && (
        <div className="flex items-center gap-1.5">
          {paid && <select value={dur} onChange={(e) => setDur(e.target.value)} className="text-[11px] bg-white border border-rose/50 rounded-lg px-1.5 py-1">{DURATIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>}
          <button onClick={apply} disabled={busy} data-testid={`apply-tier-${user.user_id}`} className="text-[11px] px-2.5 py-1 rounded-full bg-mahogany text-cream hover:opacity-90">{busy ? '…' : 'Apply'}</button>
        </div>
      )}
    </div>
  );
};

const AdminUsers = () => {
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [sel, setSel] = useState({});
  const [showBulk, setShowBulk] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api.get('/admin/memberships/plans')
      .then((r) => setPlans((r.data || []).map((p) => ({ tier_key: p.tier_key, name: p.name }))))
      .catch(() => {});
  }, []);
  const tierName = (t) => (t === 'basic' || !t) ? 'Seekers Community' : (plans.find((p) => p.tier_key === t)?.name || t);

  const selIds = Object.keys(sel).filter((k) => sel[k]);
  const deletable = users.filter((u) => !u.roles.includes('admin'));

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (roleFilter) params.role = roleFilter;
      const res = await api.get('/admin/users', { params });
      setUsers(res.data || []);
      setSel({});
    } catch (e) { console.error('Failed to load users:', e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); /* eslint-disable-next-line */ }, [roleFilter]);

  const setMembership = async (userId, tier, duration) => {
    try {
      await api.patch(`/admin/users/${userId}/membership`, { tier, duration });
      toast({ title: `Membership set to ${tierName(tier)}` });
      fetchUsers();
    } catch (e) {
      toast({ title: 'Failed to set membership', description: String(e?.response?.data?.detail || e), variant: 'destructive' });
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/admin/users/${u.user_id}/toggle-active`);
      toast({ title: `User ${u.is_active ? 'deactivated' : 'activated'}` });
      fetchUsers();
    } catch (err) { toast({ title: 'Failed', description: err?.response?.data?.detail }); }
  };

  const setRoles = async (u, roles) => {
    try {
      await api.patch(`/admin/users/${u.user_id}/roles`, roles);
      toast({ title: 'Roles updated' });
      fetchUsers();
    } catch (err) { toast({ title: 'Failed', description: err?.response?.data?.detail }); }
  };

  const toggleRole = (u, role) => {
    const has = u.roles.includes(role);
    const next = has ? u.roles.filter((r) => r !== role) : [...u.roles, role];
    setRoles(u, next.length ? next : ['member']);
  };

  const openDelete = async (u) => {
    setDeleteTarget(u); setPreview(null); setConfirmText('');
    try {
      const res = await api.get(`/admin/users/${u.user_id}/deletion-preview`);
      setPreview(res.data);
    } catch (e) { setPreview({ error: true }); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const cascade = !!preview?.has_records;
    setDeleting(true);
    try {
      const res = await api.delete(`/admin/users/${deleteTarget.user_id}`, cascade ? { params: { cascade: true } } : {});
      const freed = res.data?.seats_freed || 0;
      toast({ title: 'User deleted', description: cascade ? `${deleteTarget.email} · ${freed} seat(s) freed` : deleteTarget.email });
      setDeleteTarget(null); setPreview(null); setConfirmText('');
      fetchUsers();
    } catch (err) {
      toast({ title: 'Cannot delete user', description: err?.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setDeleting(false); }
  };

  const doBulkDelete = async () => {
    setBulkBusy(true);
    try {
      const res = await api.post('/admin/users/bulk-delete', { user_ids: selIds });
      const d = res.data || {};
      toast({ title: `Deleted ${d.deleted || 0} user(s)`, description: `${d.seats_freed || 0} seat(s) freed${d.skipped?.length ? ` · ${d.skipped.length} skipped` : ''}` });
      setShowBulk(false); setBulkText(''); setSel({});
      fetchUsers();
    } catch (err) {
      toast({ title: 'Bulk delete failed', description: err?.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setBulkBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow mb-2">Admin</div>
        <h1 className="font-serif-display text-4xl text-burgundy">Users</h1>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <form onSubmit={(e) => { e.preventDefault(); fetchUsers(); }} className="flex items-center bg-white/70 rounded-full border border-rose/50 px-4 py-2 gap-2">
          <Search className="w-4 h-4 text-mahogany/70" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email or name…"
            className="bg-transparent focus:outline-none text-burgundy w-64" />
        </form>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
          className="bg-white/70 rounded-full border border-rose/50 px-4 py-2 text-burgundy focus:outline-none">
          <option value="">All roles</option>
          {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      {selIds.length > 0 && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-2xl px-4 py-3" data-testid="users-bulk-bar">
          <span className="text-sm text-red-800 font-medium">{selIds.length} selected</span>
          <button onClick={() => { setShowBulk(true); setBulkText(''); }} data-testid="users-bulk-delete"
            className="inline-flex items-center gap-1.5 text-sm px-4 py-1.5 rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors">
            <Trash2 className="w-4 h-4" /> Delete selected
          </button>
          <button onClick={() => setSel({})} className="text-sm text-mahogany/70 hover:text-mahogany">Clear</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white/70 rounded-2xl overflow-hidden border border-rose/50">
            <thead className="bg-rose-soft/60">
              <tr className="text-left text-xs uppercase tracking-widest text-mahogany">
                <th className="px-4 py-3">
                  <input type="checkbox" data-testid="users-select-all"
                    checked={deletable.length > 0 && deletable.every((u) => sel[u.user_id])}
                    onChange={(e) => {
                      const next = {};
                      if (e.target.checked) deletable.forEach((u) => { next[u.user_id] = true; });
                      setSel(next);
                    }} />
                </th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Roles</th>
                <th className="px-4 py-3">Tier</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-t border-rose/40 hover:bg-rose-soft/20">
                  <td className="px-4 py-3">
                    {u.roles.includes('admin') ? <span className="text-mahogany/30 text-xs">—</span> : (
                      <input type="checkbox" data-testid={`user-select-${u.user_id}`}
                        checked={!!sel[u.user_id]} onChange={() => setSel((s) => ({ ...s, [u.user_id]: !s[u.user_id] }))} />
                    )}
                  </td>
                  <td className="px-4 py-3 text-burgundy font-medium">{u.full_name}</td>
                  <td className="px-4 py-3 text-mahogany/80 text-sm">{u.email}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {ROLE_OPTIONS.map((r) => (
                        <button key={r} onClick={() => toggleRole(u, r)}
                          className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${u.roles.includes(r) ? 'bg-mahogany text-cream' : 'bg-cream-2 text-mahogany/60 hover:bg-rose-soft'}`}>
                          {r}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3"><MembershipCell user={u} plans={plans} onApply={setMembership} /></td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleActive(u)} data-testid={`toggle-user-active-${u.user_id}`}
                      className={`text-xs px-3 py-1.5 rounded-full ${u.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {u.is_active ? 'Active' : 'Disabled'}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    {u.roles.includes('admin') ? (
                      <span className="text-xs text-mahogany/40">—</span>
                    ) : (
                      <button onClick={() => openDelete(u)} data-testid={`delete-user-${u.user_id}`}
                        title="Delete user"
                        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full text-red-700 bg-red-50 hover:bg-red-100 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-mahogany/60">No users found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="delete-user-modal">
          <div className="bg-cream rounded-2xl border border-rose/50 shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="font-serif-display text-2xl text-burgundy">Delete user?</h3>
            <p className="text-mahogany/80 text-sm">
              Delete <span className="font-semibold text-burgundy">{deleteTarget.full_name}</span> ({deleteTarget.email}).
            </p>

            {!preview ? (
              <div className="flex items-center gap-2 text-sm text-mahogany/70"><Loader2 className="w-4 h-4 animate-spin" /> Checking records…</div>
            ) : preview.error ? (
              <p className="text-sm text-red-700">Couldn't load this user's records. Please try again.</p>
            ) : preview.has_records ? (
              <div className="space-y-3" data-testid="delete-cascade-summary">
                <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-800">
                  This user has records. Deleting will also:
                  <ul className="list-disc ml-5 mt-2 space-y-0.5">
                    {preview.orders > 0 && <li>Archive <b>{preview.orders}</b> store order(s)</li>}
                    {preview.bookings > 0 && <li>Cancel & archive <b>{preview.bookings}</b> program booking(s)</li>}
                    {preview.transactions > 0 && <li>Archive <b>{preview.transactions}</b> payment record(s)</li>}
                    {preview.donations > 0 && <li>Archive <b>{preview.donations}</b> donation(s)</li>}
                    {preview.seats_to_free > 0 && <li>Free up <b>{preview.seats_to_free}</b> reserved seat(s) back into availability</li>}
                  </ul>
                  <div className="mt-2 text-xs">Records are archived (kept for accounting, hidden from lists), not permanently erased.</div>
                </div>
                <label className="block text-sm text-burgundy">Type <span className="font-semibold">DELETE</span> to confirm
                  <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} data-testid="delete-user-confirm-input"
                    placeholder="DELETE" className="mt-1 w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none focus:border-mahogany" />
                </label>
              </div>
            ) : (
              <p className="text-mahogany/80 text-sm">This user has no orders, bookings or payments. This action cannot be undone.</p>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => { setDeleteTarget(null); setPreview(null); setConfirmText(''); }} data-testid="cancel-delete-user"
                className="text-sm px-4 py-2 rounded-full bg-cream-2 text-mahogany hover:bg-rose-soft transition-colors">
                Cancel
              </button>
              <button onClick={confirmDelete}
                disabled={deleting || !preview || preview.error || (preview.has_records && confirmText.trim().toUpperCase() !== 'DELETE')}
                data-testid="confirm-delete-user"
                className="text-sm px-4 py-2 rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-60 inline-flex items-center gap-2">
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />} {preview?.has_records ? 'Delete & free seats' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulk && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="bulk-delete-users-modal">
          <div className="bg-cream rounded-2xl border border-rose/50 shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="font-serif-display text-2xl text-burgundy">Delete {selIds.length} user{selIds.length > 1 ? 's' : ''}?</h3>
            <p className="text-mahogany/80 text-sm">
              This permanently removes the selected accounts. Any orders, bookings or payments they have are archived (kept for accounting) and their reserved seats are freed. Admin accounts in your selection are skipped automatically. To confirm, type <span className="font-semibold text-burgundy">DELETE</span>.
            </p>
            <input value={bulkText} onChange={(e) => setBulkText(e.target.value)} data-testid="bulk-delete-users-input"
              placeholder="Type DELETE" className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none focus:border-mahogany" />
            <div className="flex justify-end gap-3 pt-1">
              <button onClick={() => { setShowBulk(false); setBulkText(''); }} data-testid="bulk-delete-users-cancel"
                className="text-sm px-4 py-2 rounded-full bg-cream-2 text-mahogany hover:bg-rose-soft transition-colors">Cancel</button>
              <button onClick={doBulkDelete} disabled={bulkBusy || bulkText.trim().toUpperCase() !== 'DELETE'} data-testid="bulk-delete-users-confirm"
                className="text-sm px-4 py-2 rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-60 inline-flex items-center gap-2">
                {bulkBusy && <Loader2 className="w-4 h-4 animate-spin" />} Delete {selIds.length} user{selIds.length > 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
